// ============================================================================
// CLIENT REGISTRATIONS MODAL
// ============================================================================
// The per-client statutory registrations drill-down — PAN, GSTIN, CIN, TAN,
// BURS-VAT and whatever a new jurisdiction brings. Opens from the Registrations
// row action on the Client master, and from the GST engagement setup modal when
// a client has no GSTIN recorded yet.
//
// WHY A DRILL-DOWN RATHER THAN A SECTION ON THE CLIENT FORM
//
// A registration needs a client_id, which does not exist until the client is
// saved, so a section on the create form would have nothing to attach to. And
// one client holds many registrations across types and jurisdictions — one PAN,
// a GSTIN per state, a CIN if incorporated — which is a list, not a field.
//
// THIS LIST IS THE SOURCE FOR GST AND FSA
//
// gst.tbl_registration extends rows in this table through a NOT NULL UNIQUE
// client_registration_id rather than keeping its own GSTIN list, and FSA's
// print headers read the same rows. What is entered here is what appears on a
// filed return and on a signed financial statement.

import React, { useState, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem } from 'components/ui';
import notificationService from 'services/notificationService';
import {
  useClientRegistrations,
  useCreateClientRegistration,
  useUpdateClientRegistration,
  useDeleteClientRegistration,
} from 'hooks/api/useClientRegistration';
import { useRegistrationTypes } from 'hooks/api/useRegistrationType';
import type {
  ClientRegistration,
  CreateClientRegistrationRequest,
  UpdateClientRegistrationRequest,
} from 'api/types';
import ClientRegistrationFormModal, {
  type ClientRegistrationFormData,
} from './ClientRegistrationFormModal';

export interface ClientRegistrationsModalProps {
  isOpen: boolean;
  clientId: string | null;
  clientName?: string | null;
  /** Actions granted on the `client-registration` screen code. */
  allowedActions?: string[];
  /**
   * Pre-select this registration type code when the Add form opens — the GST
   * setup modal passes 'GSTIN' so the person lands on the right type.
   */
  defaultRegistrationTypeCode?: string;
  onClose: () => void;
  /** Fired after any successful create/update/delete so callers can refetch. */
  onChanged?: () => void;
}

const toDateInput = (iso?: string | null): string => {
  if (!iso) return '';
  // Backend sends ISO timestamps; <input type="date"> wants yyyy-MM-dd.
  return iso.length >= 10 ? iso.slice(0, 10) : '';
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

const apiErrorMessage = (err: any, fallback: string): string => {
  const apiBody = err?.response?.data;
  if (typeof apiBody?.message === 'string' && apiBody.message) return apiBody.message;
  if (Array.isArray(apiBody?.errors) && apiBody.errors.length) return apiBody.errors.join(', ');
  if (apiBody?.errors && typeof apiBody.errors === 'object') {
    return Object.values(apiBody.errors).flat().join(', ');
  }
  return err?.message || fallback;
};

export const ClientRegistrationsModal: React.FC<ClientRegistrationsModalProps> = ({
  isOpen,
  clientId,
  clientName,
  allowedActions = [],
  defaultRegistrationTypeCode,
  onClose,
  onChanged,
}) => {
  // ---- data -------------------------------------------------------------
  const listParams = useMemo(
    () => ({
      clientId: clientId ?? undefined,
      page: 1,
      pageSize: 100,
      includeInactive: true,
    }),
    [clientId]
  );

  // Skip while closed or before a client is chosen — an unscoped request would
  // pull every registration in the firm.
  const { data, loading, refetch } = useClientRegistrations(listParams, {
    skip: !isOpen || !clientId,
  });

  // Only active types are offered for new entry; a retired type keeps working
  // on the registrations that already reference it.
  const typeParams = useMemo(() => ({ page: 1, pageSize: 200, includeInactive: false }), []);
  const { data: registrationTypes, loading: typesLoading } = useRegistrationTypes(typeParams);

  const { createClientRegistration } = useCreateClientRegistration();
  const { updateClientRegistration } = useUpdateClientRegistration();
  const { deleteClientRegistration } = useDeleteClientRegistration();

  // ---- form modal state -------------------------------------------------
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selected, setSelected] = useState<ClientRegistration | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formApiError, setFormApiError] = useState<string | null>(null);

  // ---- delete state -----------------------------------------------------
  const [deleteTarget, setDeleteTarget] = useState<ClientRegistration | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canAdd = allowedActions.includes('add');
  const canEdit = allowedActions.includes('edit');
  const canDelete = allowedActions.includes('delete');

  const handleAdd = useCallback(() => {
    setSelected(null);
    setFormMode('create');
    setFormApiError(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row: ClientRegistration) => {
    setSelected(row);
    setFormMode('edit');
    setFormApiError(null);
    setFormOpen(true);
  }, []);

  const formInitialData: ClientRegistrationFormData | null = useMemo(() => {
    if (formMode === 'create') {
      // Pre-select the requested type when the caller named one (GST passes GSTIN).
      const preset = defaultRegistrationTypeCode
        ? registrationTypes.find(
            (t) => t.code.toUpperCase() === defaultRegistrationTypeCode.toUpperCase()
          )
        : undefined;
      if (!preset) return null;
      return {
        registrationTypeId: preset.id,
        registrationNumber: '',
        jurisdiction: '',
        registrationDate: '',
        validFrom: '',
        validTo: '',
        renewalReminderDays: '',
        issuingAuthority: '',
        remarks: '',
        statusCode: 'ACTIVE',
      };
    }
    if (!selected) return null;
    return {
      registrationTypeId: String(selected.registrationTypeId),
      registrationNumber: selected.registrationNumber,
      jurisdiction: selected.jurisdiction ?? '',
      registrationDate: toDateInput(selected.registrationDate),
      validFrom: toDateInput(selected.validFrom),
      validTo: toDateInput(selected.validTo),
      renewalReminderDays:
        selected.renewalReminderDays === null || selected.renewalReminderDays === undefined
          ? ''
          : String(selected.renewalReminderDays),
      issuingAuthority: selected.issuingAuthority ?? '',
      remarks: selected.remarks ?? '',
      statusCode: (selected.statusCode as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
    };
  }, [formMode, selected, defaultRegistrationTypeCode, registrationTypes]);

  const handleSaveForm = useCallback(
    async (formData: ClientRegistrationFormData) => {
      if (!clientId) return;
      setIsSaving(true);
      setFormApiError(null);
      try {
        const common = {
          registrationTypeId: formData.registrationTypeId,
          registrationNumber: formData.registrationNumber,
          jurisdiction: formData.jurisdiction || undefined,
          registrationDate: formData.registrationDate || null,
          validFrom: formData.validFrom || null,
          validTo: formData.validTo || null,
          renewalReminderDays: formData.renewalReminderDays
            ? Number(formData.renewalReminderDays)
            : null,
          issuingAuthority: formData.issuingAuthority || undefined,
          remarks: formData.remarks || undefined,
          statusCode: formData.statusCode,
        };

        if (formMode === 'create') {
          const payload: CreateClientRegistrationRequest = { clientId, ...common };
          await createClientRegistration(payload);
          notificationService.toast.success(`Registration "${payload.registrationNumber}" added.`);
        } else if (selected) {
          const payload: UpdateClientRegistrationRequest = {
            ...common,
            rowVersion: selected.rowVersion,
          };
          await updateClientRegistration(selected.id, payload);
          notificationService.toast.success(`Registration "${payload.registrationNumber}" updated.`);
        }

        setIsSaving(false);
        setFormOpen(false);
        setSelected(null);
        refetch();
        onChanged?.();
      } catch (err: any) {
        setIsSaving(false);
        const message = apiErrorMessage(err, 'Failed to save the registration. Please try again.');
        setFormApiError(message);
        notificationService.toast.error(message, 6000);
      }
    },
    [clientId, formMode, selected, createClientRegistration, updateClientRegistration, refetch, onChanged]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteClientRegistration(deleteTarget.id);
      notificationService.toast.success(`Registration "${deleteTarget.registrationNumber}" deleted.`);
      setIsDeleting(false);
      setDeleteTarget(null);
      refetch();
      onChanged?.();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(apiErrorMessage(err, 'Failed to delete the registration.'), 6000);
    }
  }, [deleteTarget, deleteClientRegistration, refetch, onChanged]);

  // ---- grid -------------------------------------------------------------
  const columns: SyutGridColumn<ClientRegistration>[] = useMemo(
    () => [
      {
        id: 'type',
        header: 'Type',
        accessorKey: 'registrationTypeCode',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => (
          <span title={row.original.registrationTypeName}>{row.original.registrationTypeCode}</span>
        ),
      },
      {
        id: 'registrationNumber',
        header: 'Number',
        accessorKey: 'registrationNumber',
        size: 200,
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>
            {row.original.registrationNumber}
          </span>
        ),
      },
      {
        id: 'jurisdiction',
        header: 'Jurisdiction',
        accessorKey: 'jurisdiction',
        size: 150,
        enableSorting: false,
        cell: ({ row }) => row.original.jurisdiction || row.original.countryName || '-',
      },
      {
        id: 'validTo',
        header: 'Valid To',
        accessorKey: 'validTo',
        size: 140,
        enableSorting: false,
        cell: ({ row }) => {
          const { validTo, daysToExpiry } = row.original;
          if (!validTo) return <span style={{ color: 'var(--text-secondary)' }}>Evergreen</span>;
          const expired = typeof daysToExpiry === 'number' && daysToExpiry < 0;
          const dueSoon = typeof daysToExpiry === 'number' && daysToExpiry >= 0 && daysToExpiry <= 30;
          return (
            <span style={{ color: expired ? '#dc2626' : dueSoon ? '#d97706' : undefined }}>
              {formatDate(validTo)}
              {expired ? ' (expired)' : ''}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 100,
        enableSorting: false,
        cell: ({ row }) => {
          const isActive = row.original.statusCode === 'ACTIVE';
          return (
            <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
              {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
            </span>
          );
        },
      },
    ],
    []
  );

  const getRowActions = useCallback(
    (row: ClientRegistration): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];
      if (canEdit) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
        });
      }
      if (canDelete) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => setDeleteTarget(row),
          variant: 'danger',
        });
      }
      return items;
    },
    [canEdit, canDelete, handleEdit]
  );

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Registrations{clientName ? ` — ${clientName}` : ''}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {canAdd && (
              <button
                className="btn btn-primary"
                onClick={handleAdd}
                style={{ padding: '0.25rem 0.625rem', fontSize: '0.8125rem' }}
              >
                <IconSystem.add size={14} style={{ marginRight: '0.25rem' }} />
                Add
              </button>
            )}
            <button className="modal-close" onClick={onClose} title="Close">
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div
          className="modal-content"
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <p
            style={{
              margin: '0 0 0.625rem',
              fontSize: '0.75rem',
              color: 'var(--text-secondary)',
              lineHeight: 1.4,
            }}
          >
            Statutory registrations recorded for this client. These are the numbers GST files
            returns against and FSA prints on financial statements.
          </p>

          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <SyutGrid
              data={data}
              columns={columns}
              enablePagination={false}
              enableSorting={false}
              rowActions={canEdit || canDelete ? getRowActions : undefined}
              emptyMessage={
                loading
                  ? 'Loading registrations…'
                  : canAdd
                    ? 'No registrations recorded yet — use Add to record the first one.'
                    : 'No registrations recorded for this client.'
              }
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      <ClientRegistrationFormModal
        isOpen={formOpen}
        mode={formMode}
        clientName={clientName}
        registrationTypes={registrationTypes}
        registrationTypesLoading={typesLoading}
        initialData={formInitialData}
        isSaving={isSaving}
        apiError={formApiError}
        onSave={handleSaveForm}
        onClose={() => {
          setFormOpen(false);
          setSelected(null);
          setFormApiError(null);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Registration"
        message={
          deleteTarget
            ? `Delete ${deleteTarget.registrationTypeCode} "${deleteTarget.registrationNumber}"? Anything already filed against it keeps its history, but it stops being offered for new work.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
      />
    </>
  );
};

export default ClientRegistrationsModal;
